/**
 * SurfaceShaders — TSL NodeMaterial shaders for the deck, the market-floor
 * overlays, lava, water and wind.
 *
 * Uses Three.js TSL (Three Shading Language) with auto-updating `time` node.
 * No manual time uniform updates needed — TSL handles it.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, float, vec2, vec3, vec4,
  uv, positionLocal, positionWorld, time,
  sin, cos, floor, fract, dot, mix, smoothstep, step,
  uniform, normalView, positionViewDirection,
  max, min, pow, abs, oneMinus, length, fwidth, sqrt
} from 'three/tsl';

// ─── Shared Noise (TSL) ────────────────────────────────────

/**
 * A hash without `sin`. The classic `fract(sin(dot(p, k)) * 43758)` feeds
 * `sin` arguments of ~10⁴ at this arena's world coordinates, where Metal's
 * fast-math `sin` is no longer uniform — the lava's bands came out skewed
 * toward the hot end on The Chart. This is Hoskins' hash12: precise at any
 * magnitude and cheaper.
 */
const hash2d = Fn(([p_immutable]) => {
  const p = fract(vec2(p_immutable).mul(vec2(0.1031, 0.1030))).toVar();
  p.addAssign(dot(p, p.yx.add(33.33)));
  return fract(p.x.add(p.y).mul(p.x));
});

/** Two independent hashes, for a Voronoi cell's jitter. */
const hash22 = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  return vec2(hash2d(p), hash2d(p.add(vec2(17.0, 43.0))));
});

/**
 * Distance from the nearest Voronoi cell edge (F2 − F1), 3×3 cells: the
 * angular, branching fracture lines of cracked ice, which a noise contour
 * (wobbly, closed) never gives.
 */
const voronoiEdge = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  const i = floor(p);
  const f = fract(p);
  const f1 = float(8.0).toVar();
  const f2 = float(8.0).toVar();
  for (let y = -1; y <= 1; y++) {
    for (let x = -1; x <= 1; x++) {
      const g = vec2(x, y);
      const o = hash22(i.add(g));
      const r = g.add(o).sub(f);
      const d = dot(r, r);
      const closer = step(d, f1);
      f2.assign(mix(min(f2, d), f1, closer));
      f1.assign(min(f1, d));
    }
  }
  return sqrt(f2).sub(sqrt(f1));
});

const noise2d = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  const i = floor(p);
  const f = fract(p);
  const ff = f.mul(f).mul(f.mul(-2.0).add(3.0));
  const a = hash2d(i);
  const b = hash2d(i.add(vec2(1.0, 0.0)));
  const c = hash2d(i.add(vec2(0.0, 1.0)));
  const d = hash2d(i.add(vec2(1.0, 1.0)));
  return mix(mix(a, b, ff.x), mix(c, d, ff.x), ff.y);
});

// ─── Arena deck ─────────────────────────────────────────────

/**
 * The ground the match is actually played on.
 *
 * The instanced ground slab stretches one 0..1 face UV over 90 units, which
 * turned the shared brick texture into blurred 20-unit tiles — no scale cue,
 * no way to judge a jump against it. This material works in WORLD units off
 * `positionWorld`, so a grid cell is the same size no matter what mesh wears
 * it: v1's GridHelper language (fine cells + stronger majors on a dark ground)
 * rebuilt as TSL, because GridHelper itself is a legacy-material crash under
 * WebGPURenderer.
 *
 * Lit (toon) rather than unlit so platform shadows still land on the ground —
 * the shadows are half the depth read on a graybox.
 */
// Module-level: one gradient for every deck the session builds, so per-match
// arena rebuilds never leak it. Local rather than ToonMaterials' GRADIENT_3 —
// ToonMaterials imports from this file, so importing back would be a cycle.
let _deckGradient = null;
function deckGradient() {
  if (_deckGradient) return _deckGradient;
  _deckGradient = new THREE.DataTexture(new Uint8Array([0, 128, 255]), 3, 1, THREE.RedFormat);
  _deckGradient.magFilter = THREE.NearestFilter;
  _deckGradient.minFilter = THREE.NearestFilter;
  _deckGradient.needsUpdate = true;
  return _deckGradient;
}

/**
 * The colour the deck sinks toward past the zone's start radius. It follows
 * the sky's horizon (EnvironmentEffects sets it with the fog), so the rim
 * dissolves into the same colour the fog is, on every map. It used to be a
 * fixed navy that only matched the graybox sky.
 */
const _deckHorizon = uniform(new THREE.Color(0.05, 0.055, 0.115));

export function setDeckHorizon(color) {
  _deckHorizon.value.copy(color);
}

export function createDeckMaterial({ arenaRadius, centerX = 0, centerZ = 0, colorShift = null }) {
  const material = new THREE.MeshToonNodeMaterial({ color: 0xffffff, gradientMap: deckGradient() });

  /*
   * BIG PANELS, NOT A GRID.
   *
   * This drew a fine 3-unit grid with stronger majors — a graybox floor, and
   * legible, but not what `docs/reference/chart.jpg` shows. The reference deck
   * is a raised access floor: large plates about four units across, separated
   * by a thick dark seam, with a bolt at each corner. That is the same surface
   * `floorPanelTexture` gives the platforms, so drawing it here too is what
   * makes floor and furniture read as one room instead of two materials that
   * happen to share a hue.
   *
   * And it is DARK. In the reference the deck is the darkest large surface and
   * the candles are the brightest thing standing on it; ours had them at nearly
   * the same value, which is why nothing popped and the arena read as flat.
   * Value contrast is doing more work here than colour is.
   */
  const PANEL = 4.4;
  const SEAM = 0.16;
  const BOLT_INSET = 0.62;

  material.colorNode = Fn(() => {
    const px = positionWorld.x.sub(centerX);
    const pz = positionWorld.z.sub(centerZ);
    const r = length(vec2(px, pz));

    // HUE AND SATURATION ONLY. The theme's lightness term is deliberately
    // dropped here.
    //
    // A theme describes the arena's IDENTITY, which is a hue and how saturated
    // it is. Lightness is not an arena property, it is a SURFACE ROLE: the deck
    // is the dark ground and the platforms are the bright things standing on
    // it, which is the value relationship the reference has and the reason its
    // candles read as objects.
    //
    // Applying the same lightness shift to both is exactly what flattened the
    // first attempt — brightening the theme to lift the candles lifted the deck
    // with them and the contrast never changed. The deck's darkness lives in
    // the constants below, where nothing can accidentally cancel it.
    //
    // Resolved in JS rather than TSL: a handful of constants computed once at
    // build time instead of per pixel.
    const tint = (cr, cg, cb) => {
      const c = new THREE.Color(cr, cg, cb);
      if (colorShift) c.offsetHSL(colorShift[0], colorShift[1], 0);
      return vec3(c.r, c.g, c.b);
    };
    // Roughly half the old values. The toon ramp and the key light lift a big
    // upward-facing surface a long way, so a deck authored at "looks right in
    // the swatch" renders pale.
    const plate = tint(0.075, 0.115, 0.155);
    const plateAlt = tint(0.06, 0.095, 0.13);
    const seamCol = tint(0.028, 0.042, 0.06);
    const boltCol = tint(0.2, 0.27, 0.34);

    // Cell-local coordinates, centred, in world units.
    const cell = vec2(px, pz).div(PANEL);
    const cellId = floor(cell);
    const local = fract(cell).sub(0.5).mul(PANEL);
    const ax = abs(local.x);
    const az = abs(local.y);

    // Distance inward from the plate's edge, so the seam is the gap BETWEEN
    // plates rather than a line drawn on top of one.
    const edgeDist = float(PANEL * 0.5).sub(max(ax, az));
    const aa = fwidth(px).mul(1.5).add(0.01);
    const seam = oneMinus(smoothstep(float(SEAM), aa.add(SEAM), edgeDist));

    // One bolt per corner: distance to the nearest inset corner point.
    const bi = float(PANEL * 0.5 - BOLT_INSET);
    const boltD = length(vec2(ax.sub(bi), az.sub(bi)));
    const bolt = oneMinus(smoothstep(float(0.075), float(0.13), boltD));

    // Alternate plates and mottle them. A floor of identical plates reads as a
    // texture repeat; this is what stops the eye finding the tiling.
    const checker = cellId.x.add(cellId.y).mod(2.0);
    const mottle = noise2d(vec2(px, pz).mul(0.05)).mul(0.16).add(0.92);

    let color = mix(plate, plateAlt, checker).mul(mottle).toVar();
    color.assign(mix(color, seamCol, seam));
    // Bolts sit on the plate, never in the seam.
    color.assign(mix(color, boltCol, bolt.mul(oneMinus(seam))));

    // Beyond the zone's start radius the world is over; sink the deck toward
    // the horizon colour so the rim dissolves instead of ending on a hard edge.
    const edge = smoothstep(float(arenaRadius * 1.02), float(arenaRadius * 1.45), r);
    return mix(color, _deckHorizon, edge);
  })();

  return material;
}

// ─── Market-floor overlays ──────────────────────────────────

/**
 * Materials for the three floor mutators (ice / conveyor / lava), in the
 * pass-2 language: drawn shapes with hard cel bands and ink lines, not a
 * tinted wash. One material per kind for the whole session, shared by the
 * arena circle and every platform quad, so an activation costs zero pipeline
 * compiles after the first — and ArenaRenderer PREWARMS that first one at map
 * load, against the real render target, because the first draw of an
 * uncompiled overlay cost a 480 ms frame mid-fight (docs/design/vfx3/MARKET.md).
 *
 * Arrival and removal are uniform-driven, allocation-free:
 *
 *   sweep   the wipe radius from `center`, in world units. The overlay exists
 *           inside it, with a white shock band trailing the edge. MarketFx
 *           runs it 0 → past the rim in FLOOR_SWEEP_MS. Things POP in.
 *   erode   -0.15 = whole, 1.15 = gone: `alpha = smoothstep(noise)` with a
 *           bright front on the erosion edge. Things ERODE out.
 *
 * `strength` is the rim/global multiplier (1 in play). `radius` is set per
 * map so the overlays fade out where the deck does.
 */
const _chaosMats = {};

export function getChaosOverlayMaterials() {
  if (_chaosMats.ice) return _chaosMats;

  const strength = uniform(1);
  const radius = uniform(45);
  const center = uniform(new THREE.Vector2(0, 0));
  const sweep = uniform(1e4);
  const erode = uniform(-0.15);
  Object.assign(_chaosMats, { strength, radius, center, sweep, erode });

  const white = vec3(1.0);

  /** The mask every overlay shares: rim fade × wipe × erosion, plus fronts. */
  const common = () => {
    const p = vec2(positionWorld.x, positionWorld.z);
    const rel = p.sub(center);
    const r = length(rel);
    const rim = oneMinus(smoothstep(radius, radius.mul(1.3), r));
    // The wipe: a hard-ish edge (0.6 u of anti-aliasing) with a 1.6 u hot
    // band trailing it so the front reads as a shock, not a reveal.
    const wipe = oneMinus(smoothstep(sweep.sub(0.6), sweep.add(0.2), r));
    const front = smoothstep(sweep.sub(2.2), sweep.sub(0.6), r).mul(oneMinus(smoothstep(sweep.sub(0.2), sweep.add(0.3), r)));
    // The erosion: two-tap noise, a tight edge, a bright band on the front.
    const en = noise2d(p.mul(0.33)).mul(0.7).add(noise2d(p.mul(1.1).add(3.7)).mul(0.3));
    const alive = smoothstep(erode.sub(0.03), erode.add(0.03), en);
    const eFront = smoothstep(erode.sub(0.14), erode, en).mul(oneMinus(smoothstep(erode, erode.add(0.05), en)));
    const mask = wipe.mul(alive).mul(rim).mul(strength);
    const hot = max(front, eFront);
    return { p, rel, r, mask, hot };
  };

  // ICE — crisp cel glaze: two plate tones, sharp diagonal highlights, ink
  // cracks in two scales. Half-transparent so the deck's plates and bolts
  // still read through it; it is a glaze on the floor, not a new floor.
  //
  // The cracks are LINES, a pixel or two wide whatever the distance
  // (`fwidth`), branching at two frequencies; the first pass cut them as a
  // fixed noise threshold and they came out as fat blue river channels.
  {
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const { p, mask, hot } = common();
    const plate = step(0.5, noise2d(p.mul(0.085).add(1.3)));
    // Long thin highlights along one diagonal, in two sizes, barely drifting.
    const along = dot(p, vec2(0.83, 0.55));
    const across = dot(p, vec2(-0.55, 0.83));
    const sA = noise2d(vec2(along.mul(0.05), across.mul(1.1)).add(time.mul(0.015)));
    const streakA = smoothstep(0.66, 0.70, sA);
    const streakB = step(0.78, noise2d(vec2(along.mul(0.11).add(7.0), across.mul(2.2))));
    const streak = max(streakA, streakB);
    // Cracks: Voronoi cell edges in two scales (angular, branching), and they
    // CLUSTER — a slow mask keeps them to about a third of the deck, so they
    // read as damage rather than as a net over everything.
    // Big cells (about six units) where the mask says so, finer branching in
    // the densest third of that: a few fractures, not a net over the deck.
    const where = noise2d(p.mul(0.045).add(1.0));
    const e1 = voronoiEdge(p.mul(0.17));
    const w1 = max(fwidth(e1).mul(1.6), 0.02);
    const crack = oneMinus(smoothstep(float(0.012), float(0.012).add(w1), e1)).mul(step(0.6, where));
    const e2 = voronoiEdge(p.mul(0.42).add(5.0));
    const w2 = max(fwidth(e2).mul(1.4), 0.018);
    const crack2 = oneMinus(smoothstep(float(0.01), float(0.01).add(w2), e2)).mul(step(0.74, where));
    const cracks = max(crack, crack2);
    const pale = vec3(0.62, 0.84, 0.96);
    const paler = vec3(0.86, 0.95, 1.0);
    const ink = vec3(0.05, 0.10, 0.16);
    let color = mix(pale, paler, plate);
    color = mix(color, white, streak);
    color = mix(color, ink, cracks.mul(0.92));
    color = mix(color, white, hot.mul(0.9));
    material.colorNode = color;
    material.opacityNode = float(0.48).add(plate.mul(0.12)).add(streak.mul(0.8)).add(cracks.mul(0.45)).add(hot.mul(0.5)).min(1.0).mul(mask);
    material.fog = true;
    _chaosMats.ice = material;
  }

  // CONVEYOR — chunky chevrons marching along the drift, apex first: an ink
  // outline, an amber body and a cream leading edge on every V. Faster than
  // the real drift (×2.4) so the direction reads from across the arena.
  {
    const dir = uniform(new THREE.Vector2(1, 0));
    const speed = uniform(1);
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const { rel, mask, hot } = common();
    const along = dot(rel, dir);
    // The V repeats ACROSS the drift too (a triangle wave every LANE units),
    // so the deck is a field of belt arrows rather than two sets of diagonal
    // stripes meeting at one seam through the arena centre.
    const LANE = 4.6;
    const SPACING = 4.4;
    const acrossRaw = dot(rel, vec2(dir.y.negate(), dir.x));
    const across = abs(fract(acrossRaw.div(LANE).add(0.5)).sub(0.5)).mul(LANE);
    const a = along.add(across.mul(0.9)).sub(time.mul(speed).mul(2.4));
    const c = fract(a.div(SPACING));
    const aa = fwidth(c).mul(1.5).add(0.004);
    const body = smoothstep(float(0.66).sub(aa), float(0.66).add(aa), c).mul(oneMinus(smoothstep(float(0.86).sub(aa), float(0.86).add(aa), c)));
    const lead = smoothstep(float(0.82).sub(aa), float(0.82).add(aa), c).mul(oneMinus(smoothstep(float(0.86).sub(aa), float(0.86).add(aa), c)));
    const inkA = oneMinus(smoothstep(aa, aa.mul(2.4), abs(c.sub(0.66))));
    const inkB = oneMinus(smoothstep(aa, aa.mul(2.4), abs(c.sub(0.86))));
    const ink = max(inkA, inkB);
    const amber = vec3(1.0, 0.64, 0.14);
    const cream = vec3(1.0, 0.96, 0.82);
    const inkC = vec3(0.10, 0.05, 0.02);
    let color = mix(amber, cream, lead);
    color = mix(color, inkC, ink);
    color = mix(color, white, hot.mul(0.85));
    material.colorNode = color;
    material.opacityNode = body.mul(0.74).add(ink.mul(0.9)).add(0.03).min(1.0).mul(mask);
    material.fog = true;
    _chaosMats.conveyor = material;
    _chaosMats.conveyorDir = dir;
    _chaosMats.conveyorSpeed = speed;
  }

  // LAVA — cel-banded molten flow: four hard bands (crust, red, orange, hot)
  // with ink crust lines between them, a slow pulse on the hot band, and
  // ember pops: jittered dots that blink white-hot above the bloom threshold.
  {
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const { p, mask, hot } = common();
    // Three octaves so the band edges wobble at the small scale too: two
    // read as smooth blobs, which is a lava lamp, not a flow.
    const f = noise2d(p.mul(0.10).add(time.mul(0.06))).mul(0.55)
      .add(noise2d(p.mul(0.21).sub(time.mul(0.10))).mul(0.32))
      .add(noise2d(p.mul(0.55).add(time.mul(0.04))).mul(0.13));
    const aa = fwidth(f).mul(1.2).add(0.003);
    const band = (edge) => smoothstep(float(edge).sub(aa), float(edge).add(aa), f);
    const line = (edge) => oneMinus(smoothstep(aa, aa.mul(2.0), abs(f.sub(edge))));
    const b1 = band(0.27);
    const b2 = band(0.50);
    const b3 = band(0.72);
    // Authored a stop darker than they should look: exposure 1.25 and ACES
    // lift an unlit orange to cream, so the orange band is a deep orange and
    // the hot band a yellow-orange, and both land where they were meant to.
    const crust = vec3(0.10, 0.025, 0.02);
    const red = vec3(0.50, 0.05, 0.01);
    const orange = vec3(0.90, 0.30, 0.02);
    const hotC = vec3(1.0, 0.62, 0.10);
    let color = mix(crust, red, b1);
    color = mix(color, orange, b2);
    color = mix(color, hotC, b3);
    const inkLine = max(max(line(0.27), line(0.50)), line(0.72));
    color = mix(color, vec3(0.08, 0.02, 0.01), inkLine.mul(0.9));
    color = color.mul(float(1.0).add(b3.mul(sin(time.mul(2.6)).mul(0.08))));
    // Ember pops. One candidate per 2.2 u cell, jittered off the cell centre,
    // lit a tenth of the time on its own phase, only over molten rock — a
    // sparse crackle, not confetti.
    const cell = floor(p.mul(0.45));
    const h = hash2d(cell);
    const h2 = hash2d(cell.add(11.0));
    const cp = fract(p.mul(0.45)).sub(0.5).add(vec2(h.sub(0.5).mul(0.7), h2.sub(0.5).mul(0.7)));
    const blink = step(0.9, fract(h.mul(19.7).add(time.mul(h.mul(0.5).add(0.3)))));
    const dotMask = oneMinus(step(float(0.05).add(h.mul(0.03)), length(cp)));
    const ember = dotMask.mul(blink).mul(step(0.5, f));
    color = color.add(vec3(1.0, 0.85, 0.5).mul(ember).mul(2.6));
    color = mix(color, white, hot.mul(0.85));
    material.colorNode = color;
    material.opacityNode = float(0.96).mul(mask);
    material.fog = true;
    _chaosMats.lava = material;
  }

  return _chaosMats;
}

/** Whole-to-gone erosion mapped onto the overlay's uniform range. */
export function overlayErodeValue(progress01) {
  return -0.15 + Math.max(0, Math.min(1, progress01)) * 1.3;
}

// ─── Per-frame hooks ────────────────────────────────────────

/**
 * main.js calls `updateConveyorScrolls` every render frame; that call is the
 * one clock this module is guaranteed to receive. Arena-side animations (the
 * market-floor transitions) register here instead of adding another main.js hook.
 */
const _frameHooks = new Set();

export function registerSurfaceFrameHook(fn) {
  _frameHooks.add(fn);
  return () => _frameHooks.delete(fn);
}

// ─── Platform rims ──────────────────────────────────────────

const _rimMaterialCache = new Map();

/**
 * Unlit border line for a raised platform's top face.
 *
 * A graybox platform is a dark toon box whose side faces sit in the deepest
 * gradient band — from eye height its silhouette is a black slab against a
 * black slab, and there is no line telling you where the landable top ends.
 * The rim is that line. Unlit on purpose: it has to read at the far side of
 * the arena, where a lit line would fall into the same shadow as the box.
 *
 * Built from `positionLocal` (the quad is authored at world scale) so the
 * border is a constant 0.22 units on any footprint; cached by quantized size
 * because each distinct footprint bakes its half-extents into the node graph.
 */
export function createPlatformRimMaterial(halfX, halfZ) {
  const key = `${halfX.toFixed(2)}|${halfZ.toFixed(2)}`;
  const cached = _rimMaterialCache.get(key);
  if (cached) return cached;

  const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });

  // An EDGE, not a border.
  //
  // At 0.22 wide and 0.85 opaque this was a thick white frame inset into every
  // platform top — from the match camera it read as a sticker applied to each
  // box rather than as light catching a corner. The reference has a bright
  // edge one or two pixels wide where the top face meets the side, and nothing
  // else; the top face itself is simply lit.
  //
  // It also scales with the platform now. A fixed 0.22 is a third of a 1.5-wide
  // candle's half-width and a twentieth of an island's, so the same constant
  // read as a hairline on one and a painted margin on the other.
  const BORDER = Math.min(0.09, Math.min(halfX, halfZ) * 0.14);
  material.colorNode = vec3(0.62, 0.86, 0.98);
  material.opacityNode = Fn(() => {
    const edgeX = float(halfX).sub(abs(positionLocal.x));
    const edgeZ = float(halfZ).sub(abs(positionLocal.y));
    const d = min(edgeX, edgeZ);
    const aa = fwidth(d).add(0.01);
    return oneMinus(smoothstep(float(BORDER), float(BORDER).add(aa), d)).mul(0.55);
  })();

  _rimMaterialCache.set(key, material);
  return material;
}

export function clearPlatformRimMaterials() {
  for (const mat of _rimMaterialCache.values()) mat.dispose();
  _rimMaterialCache.clear();
}

// ─── Lava ───────────────────────────────────────────────────

export function createLavaShaderMaterial() {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;

  // Vertex: displacement via noise
  const pos = positionLocal.toVar();
  const n = noise2d(pos.xz.mul(0.3).add(time.mul(0.4))).mul(0.4)
    .add(noise2d(pos.xz.mul(0.7).sub(time.mul(0.3))).mul(0.2));
  material.positionNode = pos.add(vec3(0, n, 0));

  // Fragment: color ramp
  const uvScaled = uv().mul(6.0);
  const n1 = noise2d(uvScaled.add(time.mul(0.3)));
  const n2 = noise2d(uvScaled.mul(2.0).sub(time.mul(0.5)));
  const combined = n1.mul(0.6).add(n2.mul(0.4));

  const darkRed = vec3(0.5, 0.05, 0.0);
  const orange = vec3(0.9, 0.3, 0.0);
  const yellow = vec3(1.0, 0.8, 0.2);
  let color = mix(darkRed, orange, smoothstep(0.2, 0.5, combined));
  color = mix(color, yellow, smoothstep(0.6, 0.85, combined));

  // Bright crack lines
  const crack = smoothstep(0.78, 0.82, combined);
  color = color.add(vec3(1.0, 0.9, 0.4).mul(crack).mul(0.8));

  // Pulsing glow
  color = color.mul(float(0.9).add(sin(time.mul(2.0)).mul(0.1)));

  material.colorNode = color;
  material.opacityNode = float(0.9);

  return material;
}

// ─── Water ──────────────────────────────────────────────────

export function createWaterShaderMaterial() {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;

  // Vertex: multi-frequency sine waves
  const pos = positionLocal.toVar();
  const wave = sin(pos.x.mul(1.5).add(time.mul(2.0))).mul(0.15)
    .add(sin(pos.z.mul(2.0).sub(time.mul(1.5))).mul(0.1))
    .add(sin(pos.x.add(pos.z).mul(0.8).add(time)).mul(0.08));
  material.positionNode = pos.add(vec3(0, wave, 0));

  // Fragment: Fresnel + shimmer
  const fresnel = pow(oneMinus(max(dot(normalView, positionViewDirection), 0.0)), 2.0);

  const deepBlue = vec3(0.05, 0.15, 0.4);
  const surfaceTeal = vec3(0.1, 0.5, 0.6);
  const highlight = vec3(0.4, 0.8, 0.9);

  const uvCoord = uv();
  let color = mix(deepBlue, surfaceTeal, uvCoord.y.mul(0.5).add(0.5));

  // Shimmer
  const shimmer = sin(uvCoord.x.mul(20.0).add(time.mul(3.0))).mul(sin(uvCoord.y.mul(15.0).sub(time.mul(2.0))));
  color = color.add(highlight.mul(smoothstep(0.7, 1.0, shimmer)).mul(0.3));

  const alpha = mix(float(0.6), float(0.85), fresnel);

  material.colorNode = color;
  material.opacityNode = alpha;

  return material;
}

// ─── Wind ───────────────────────────────────────────────────

export function createWindShaderMaterial(windForce = [1, 0, 0]) {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;
  material.depthWrite = false;
  material.blending = THREE.AdditiveBlending;

  const windDir = uniform(new THREE.Vector3(...windForce).normalize());

  // Fragment: scrolling dashes in wind direction
  const uvCoord = uv();
  const dir = windDir.xz.normalize();
  const projected = dot(uvCoord.sub(0.5), dir);
  const dashRaw = sin(projected.mul(15.0).sub(time.mul(4.0)).mul(3.14159)).mul(0.5).add(0.5);
  const dash = smoothstep(0.6, 0.8, dashRaw);

  // Fade at edges
  const edge = smoothstep(0.0, 0.15, uvCoord.x)
    .mul(smoothstep(1.0, 0.85, uvCoord.x))
    .mul(smoothstep(0.0, 0.15, uvCoord.y))
    .mul(smoothstep(1.0, 0.85, uvCoord.y));

  const windColor = vec3(0.6, 0.85, 1.0);
  const windAlpha = dash.mul(edge).mul(0.15);

  material.colorNode = windColor;
  material.opacityNode = windAlpha;

  return material;
}

// ─── Conveyor (CPU-based, unchanged) ────────────────────────

const conveyorMaterials = [];

export function registerConveyorMaterial(material, speed, direction) {
  conveyorMaterials.push({ material, speed, direction });
}

export function updateConveyorScrolls(delta) {
  for (const entry of conveyorMaterials) {
    if (entry.material.map) {
      const dir = entry.direction || [1, 0, 0];
      entry.material.map.offset.x += dir[0] * entry.speed * delta * 0.1;
      entry.material.map.offset.y += dir[2] * entry.speed * delta * 0.1;
    }
  }
  for (const fn of _frameHooks) fn(delta);
}

/**
 * Drops the registry when an arena is torn down.
 *
 * v1 never needed this — one client, one arena, one page load. v2 rebuilds the
 * arena per match, and without this the list grows by every conveyor in every
 * map the session ever loaded, animating textures nothing is drawing.
 */
export function clearConveyorMaterials() {
  conveyorMaterials.length = 0;
}
